/* eslint-disable no-process-env */
import { execute, GraphQLRequest } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import envDetect from '@tcc/env-detect';
import * as wam from '@tcc/wam';

import { handleErrors } from '@tcc/scarecrow';
import { getUserData } from './wam';
import { shouldUseMocks } from './mocks';
import { getApiUrl } from './url';
import { DataLegitimacy } from './dataLegitimacy';

interface RequestOptions {
  headers?: Record<string, string | number>;
  mockFile?: string;
  rootUrl?: string;
  fullResponse?: boolean;
  removeCredentials?: boolean;
  useMocks?: boolean;
  isFormData?: boolean;
  data?: Record<string, string> | FormData;
  isPlugin?: boolean;
}
interface ApolloOptions {
  version?: number;
  dataLegitimacy?: DataLegitimacy;
}

const tccHeaders = {
  'X-http-caller-id': 'X-TCC-DMB',
  'X-Tcc-Site': wam.getSiteCode(),
  'x-authenticated-user-id': getUserData() ? getUserData().clientId : null,
};

export function getHeaders(headers, isFormData) {
  if (isFormData) {
    return { ...tccHeaders, ...headers };
  }
  return {
    ...tccHeaders,
    ...headers,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
}

/**
 * Check the Response Headers and convert it to JSON if valid
 * @param {Promise} response
 * @return {Promise}
 */
export function toJSON(response) {
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  throw Error('Unable to convert response to JSON');
}

export function requestGet(path, opts: RequestOptions = {}) {
  const { headers, mockFile, rootUrl, fullResponse, removeCredentials } = opts;
  
  // You can use mocks for a single request by setting the opts.useMocks
  if ((shouldUseMocks() || opts.useMocks) && mockFile) {
    // eslint-disable-next-line import/no-dynamic-require, global-require, @typescript-eslint/no-var-requires
    return require(`../mocks/${mockFile}`);
  }
  return fetch(getApiUrl(path, rootUrl), {
    credentials: removeCredentials ? undefined : 'include',
    headers: { ...tccHeaders, ...headers },
  }).then((response) => {
    // CFVWEB-8406 Remove this if block when scarecrow is updated and deployed to prod
    if (response.status >= 100 && response.status < 400) {
      if (fullResponse) {
        return Promise.resolve(response);
      }
      const contentType = response.headers.get('content-type');
      const isJSONResponse = contentType
        ? contentType.includes('application/json')
        : false;
      return isJSONResponse
        ? response.text().then((text) => (text ? JSON.parse(text) : {}))
        : Promise.resolve(response.statusText);
    }
    return handleErrors(response, fullResponse);
  });
}

export function requestLegendJSON(path, opts: RequestOptions = {}) {
  const { mockFile, rootUrl } = opts;

  // You can use mocks for a single request by setting the opts.useMocks
  if ((shouldUseMocks() || opts.useMocks) && mockFile) {
    // eslint-disable-next-line import/no-dynamic-require, global-require, @typescript-eslint/no-var-requires
    return require(`../mocks/${mockFile}`);
  }

  return fetch(getApiUrl(path, rootUrl), {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).then(handleErrors);
}

export function requestPost(path, opts: RequestOptions = {}) {
  const { data, headers, mockFile, rootUrl, isFormData } = opts;

  // You can use mocks for a single request by setting the opts.useMocks
  if ((shouldUseMocks() || opts.useMocks) && mockFile) {
    // eslint-disable-next-line import/no-dynamic-require, global-require, @typescript-eslint/no-var-requires
    return require(`../mocks/${mockFile}`);
  }

  return fetch(getApiUrl(path, rootUrl), {
    method: 'post',
    credentials: 'include',
    headers: getHeaders(headers, isFormData),
    body: isFormData ? (data as unknown as FormData) : JSON.stringify(data),
  }).then(handleErrors);
}

export function requestPut(path, opts: RequestOptions = {}) {
  const { data, headers, mockFile, rootUrl } = opts;

  // You can use mocks for a single request by setting the opts.useMocks
  if ((shouldUseMocks() || opts.useMocks) && mockFile) {
    // eslint-disable-next-line import/no-dynamic-require, global-require, @typescript-eslint/no-var-requires
    return require(`../mocks/${mockFile}`);
  }

  return fetch(getApiUrl(path, rootUrl), {
    method: 'put',
    credentials: 'include',
    headers: {
      ...tccHeaders,
      ...headers,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  }).then(handleErrors);
}

export function requestLegendPut(path, opts: RequestOptions = {}) {
  const { mockFile, data, rootUrl } = opts;

  // You can use mocks for a single request by setting the opts.useMocks
  if ((shouldUseMocks() || opts.useMocks) && mockFile) {
    // eslint-disable-next-line import/no-dynamic-require, global-require, @typescript-eslint/no-var-requires
    return require(`../mocks/${mockFile}`);
  }

  return fetch(getApiUrl(path, rootUrl), {
    method: 'put',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  }).then(handleErrors);
}

export function requestDelete(path, opts: RequestOptions = {}) {
  const { data, headers, mockFile, rootUrl } = opts;

  // You can use mocks for a single request by setting the opts.useMocks
  if ((shouldUseMocks() || opts.useMocks) && mockFile) {
    // eslint-disable-next-line import/no-dynamic-require, global-require, @typescript-eslint/no-var-requires
    return require(`../mocks/${mockFile}`);
  }

  return fetch(getApiUrl(path, rootUrl), {
    method: 'delete',
    credentials: 'include',
    headers: {
      ...tccHeaders,
      ...headers,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  }).then(handleErrors);
}

const getGqlUri = (version = 1) => {
  if (envDetect.getCurrentEnv() === envDetect.environments.PRODUCTION) {
    return `https://ol.na.climate.com/v${version}/graphql`;
  }
  return `/v${version}/graphql`;
};

const getApolloLink = (options: ApolloOptions = {}) =>
  new HttpLink({
    uri: getGqlUri(options.version),
    headers: {
      'x-http-caller-id': process.env.npm_package_name,
      'x-http-caller-version': process.env.npm_package_version,
      'content-type': 'application/json',
      'x-data-legitimacy': options.dataLegitimacy || DataLegitimacy.LEGITIMATE,
    },
    credentials: 'include',
  });

export function requestGraphQL(request: GraphQLRequest, options: object = {}) {
  return new Promise((resolve, reject) =>
    /* eslint-disable-next-line no-promise-executor-return */
    execute(getApolloLink(options), request).subscribe({
      next: resolve,
      error: reject,
    })
  );
}
